<script lang="ts">
  import type { SPFocus } from '../../Util/api';
  import type { MedianAndWeatherStores, MyFitnessStores } from '../stores';
  import type { ForecastActivityState } from '@/Common/constants';
  import { Activity } from '@/Common/ActivityFunctions';
  import { getForecastActivityState } from '@/Common/forecastActivityState';
  import ForecastActivityExplanation from '@/Common/ForecastActivityExplanation.svelte';
  import UpcomingTrainingActivityCard from './UpcomingTrainingActivityCard.svelte';
  import AdaptForecastDialog from '../Calendar/AdaptForecastDialog.svelte';
  import { Button } from '@/lib/components/ui/button';
  import { XfaiBall } from '@/Common';
  import { addDays } from '@/Util/utils';

  export let myFitness: MyFitnessStores;
  export let forecast_activities;
  export let activities_3day: any[][] | null = null;
  export let spFocus_3day: SPFocus[] | null = null;
  export let lastBTDate: Date | null = null;
  export let isForecastUpToDate: boolean;
  export let lastForecastInputs: any;
  export let isLoading: boolean = false;

  let adaptForecastModal;

  $: days = activities_3day.map((activities, index) => {
    return {
      date: activities[0]?.start_date ? new Date(activities[0].start_date) : addDays(new Date(), index + 1),
      forecasted: activities.find((a) => Activity.isForecast(a)),
      planned: activities.filter((a) => Activity.isPlanned(a) && !Activity.isForecast(a)),
      completed: activities.filter((a) => Activity.isRecorded(a)),
    };
  });

  let { trainingPlan } = myFitness;

  let forecastInfo: (ForecastActivityState | undefined)[];
  $: forecastInfo = [0, 1, 2].map((i) => getForecastActivityState(forecast_activities?.[i], $trainingPlan, spFocus_3day?.[i]?.sig, lastBTDate));

  function daySumXSS(day) {
    const items = [day.forecasted, ...day.planned].filter(Boolean);
    return items.reduce(
      (acc, it) => {
        acc.xlss += it.xlss ?? 0;
        acc.xhss += it.xhss ?? 0;
        acc.xpss += it.xpss ?? 0;
        acc.xss += it.xss ?? 0;
        return acc;
      },
      { xlss: 0, xhss: 0, xpss: 0, xss: 0 },
    );
  }

  function dayLabel(date: Date, idx: number) {
    if (idx === 0) return 'Tomorrow';
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    return capitalize(date?.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) ?? '');
  }

</script>

<aside class="w-full shrink-0 space-y-4">
  <div class='flex flex-row items-center justify-between'>
    <div class="px-2 text-xs uppercase tracking-wide text-gray-500">Next 3 days</div>
    <Button variant='outline' on:click={() => adaptForecastModal.openModal()} class='group/crysball inline-flex justify-center items-center' id='adapt-forecast-btn'>
      <span class='relative px-[2px] pt-1 mr-1'>
        <XfaiBall class="h-5" type="hover"/>
        <div class="red-dot" hidden={isForecastUpToDate} />
      </span>
      <span class='extra-text2'>Adapt Forecast</span>
    </Button>
  </div>
  {#each days as day, i}
    {@const total = daySumXSS(day)}
    <section class="rounded-lg border border-border bg-white p-3 shadow-sm hover:shadow">
      <div class="mb-2 flex items-start text-sm">
        {#if isLoading}
          <i class="fa-solid fa-spinner-third fa-spin text-gray-400 mr-2 mt-1" />
        {/if}
        <div class="font-medium">{dayLabel(day.date, i)}</div>
        <div class='flex-1'></div>
        <!-- TODO (hyde): add badge for completed days -->
        <!-- <i class='fa-solid fa-badge-check text-[#7ed321]' /> -->
      </div>
      {#if day.forecasted}
        <UpcomingTrainingActivityCard activity={day.forecasted} boldTitle />
      {/if}
      {#if day.planned.length > 0}
        {#each day.planned as p}
          <UpcomingTrainingActivityCard activity={p} />
        {/each}
      {/if}
      {#if day.planned.length === 0 && !day.forecasted}
        <div class="text-xs text-gray-500">No planned activities</div>
      {/if}

      <ForecastActivityExplanation activity={forecast_activities?.[i]} forecastInfo={forecastInfo[i]} {myFitness} />
    </section>
  {/each}
</aside>

<AdaptForecastDialog bind:this={adaptForecastModal} forecastData={lastForecastInputs} today={new Date()} />

<style lang='scss'>
  .red-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: red;
    position: absolute;
    top: 2px;
    right: 2px;
  }
</style>
